import { startsWith, has, cloneDeep } from 'lodash-es'

export default function () {
  function scrollTo (name, offset = 0) {
    const el = document.getElementById(name)
    if (!el) return
    const rect = el.getBoundingClientRect()
    const scrollOffset = window.pageYOffset + rect.top + offset
    window.scrollTo({
      top: scrollOffset,
      behavior: 'smooth'
    })
  }

  function loadScript (src, async = true, defer = false, callback = null, crossOrigin = true) {
    const script = document.createElement('script')
    script.src = src
    if (async) script.async = true
    if (defer) script.defer = true
    script.onload = callback
    if (crossOrigin) script.crossOrigin = 'anonymous'
    document.head.appendChild(script)
  }

  function addServiceKey (key, value) {
    window.serviceKeys = window.serviceKeys || {}
    window.serviceKeys[key] = value
  }

  function getCookieParams (params) {
    const config = useRuntimeConfig()
    const secure = startsWith(config.public.APP_ROOT, 'https')

    return {
      path: '/',
      sameSite: secure ? 'none' : 'strict',
      secure,
      ...params
    }
  }

  function formatPrice (amount, symbol = '$') {
    const price = Number(amount / 100).toFixed(2)
    const value = parseInt(price) === Number(price) ? parseInt(price) : price

    return `${symbol}${value}`
  }

  function formatTitle (string, emClass = '') {
    if (!string) return ''
    return string.replace(/\[/g, `<em${emClass?.length ? ` class="${emClass}"` : ''}>`).replace(/\]/g, '</em>')
  }

  function emphasizeChapter (str) {
    return str.replace(/(Chapter \d+): /g, '<em>$1:</em> ')
  }

  function getFlagEmoji (countryCode) {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt())
    return String.fromCodePoint(...codePoints)
  }

  function downloadBlob (blob, name) {
    // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
    const blobUrl = URL.createObjectURL(blob)

    // Create a link element
    const link = document.createElement('a')

    // Set link's href to point to the Blob URL
    link.href = blobUrl
    link.download = name

    // Append link to the body
    document.body.appendChild(link)

    // Dispatch click event on the link
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      })
    )

    // Remove link from body
    document.body.removeChild(link)
  }

  async function scheduleTimeout (datetimeString, callback) {
    const now = await useExams().now()
    const targetDatetime = new Date(datetimeString).getTime()
    const timeoutDuration = Math.max(targetDatetime - now, 0)

    return setTimeout(() => {
      console.info('Timeout reached!')
      callback()
    }, timeoutDuration)
  }

  function removeFilesKeysFromLocalStorage (value) {
    const keysToRemove = []

    // Iterate through all keys in Local Storage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)

      if (key.startsWith(value)) {
        keysToRemove.push(key)
      }
    }

    // Remove the keys
    keysToRemove.forEach(key => {
      console.info(`Removing from localStorage: ${key}`)
      localStorage.removeItem(key)
    })
  }

  function formatDate (dateString, { withTime = false } = {}) {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    const options = { year: 'numeric', month: 'long', day: 'numeric' }
    if (withTime) {
      options.hour = '2-digit'
      options.minute = '2-digit'
      options.hour12 = false
    }
    return date.toLocaleDateString('en-US', options)
  }

  function formatDateShort (dateString) {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()

    return `${month}/${day}/${year}`
  }

  function parseMarkdown (md) {
    if (import.meta.server) {
      return import('@nuxt/content/transformers/markdown').then(mod => {
        return mod.default.parse('custom.md', md)
      })
    }
    return md
  }

  function countdownTransform (originalProps) {
    if (
      !has(originalProps, 'days') ||
      !has(originalProps, 'hours') ||
      !has(originalProps, 'minutes') ||
      !has(originalProps, 'seconds')
    ) {
      return { days: '00', hours: '00', minutes: '00', seconds: '00' }
    }

    const props = cloneDeep(originalProps)
    Object.entries(props).forEach(([key, value]) => {
      const digits = value < 10 ? `0${value}` : String(value)
      props[key] = digits
    })
    return props
  }

  function countdownTransformDaysToHours (originalProps) {
    if (
      !has(originalProps, 'days') ||
      !has(originalProps, 'hours') ||
      !has(originalProps, 'minutes') ||
      !has(originalProps, 'seconds')
    ) {
      return { hours: '00', minutes: '00', seconds: '00' }
    }

    const props = cloneDeep(originalProps)

    // Convert days to hours
    if (props.days) {
      props.hours = props.hours + (props.days * 24)
    }

    // Format only hours, minutes, seconds (no days)
    const result = {
      hours: props.hours < 10 ? `0${props.hours}` : String(props.hours),
      minutes: props.minutes < 10 ? `0${props.minutes}` : String(props.minutes),
      seconds: props.seconds < 10 ? `0${props.seconds}` : String(props.seconds)
    }

    return result
  }

  function extractSubstring (originalString, identifier) {
    const startPos = originalString.indexOf(identifier)
    if (startPos !== -1) {
      return originalString.substring(startPos)
    }
    return originalString
  }

  function alternateSplitArray (elements) {
    const firstGroup = []
    const secondGroup = []

    elements.forEach((card, index) => {
      if (index % 2 === 0) {
        firstGroup.push(card)
      } else {
        secondGroup.push(card)
      }
    })
    return [firstGroup, secondGroup]
  }

  function getFlashMessageKey (tech) {
    return `${tech?.id?.toUpperCase() || 'CERT'}_SIGN_UP_COMPLETED`
  }

  function getShortName (firstname, lastname) {
    if (!firstname && !lastname) return ''
    if (!firstname) return lastname.slice(0, 2)
    if (!lastname) return firstname.slice(0, 2)

    return `${firstname[0]}${lastname[0]}`
  }

  function isEditorPage () {
    if (import.meta.server) return false
    const route = useRoute()
    return route.path.includes('/editor/')
  }

  function isPastDate (dateStr) {
    if (!dateStr) return false
    const date = new Date(dateStr)
    if (isNaN(date)) return false
    const now = new Date()
    return date < now
  }

  /**
   * Builds intended URL by removing uid from query params
   * @param {Object} route - Route object with path and query
   * @returns {string} Intended URL without uid parameter
   */
  function buildIntendedUrl (route) {
    if (!route || !route.path) {
      return '/'
    }

    const intendedPath = route.path
    const intendedQuery = { ...route.query }
    delete intendedQuery.uid

    // Use URLSearchParams for robust query string construction
    const queryKeys = Object.keys(intendedQuery)
    if (queryKeys.length === 0) {
      return intendedPath
    }

    const searchParams = new URLSearchParams()
    queryKeys.forEach(key => {
      const value = intendedQuery[key]
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value))
      }
    })

    const queryString = searchParams.toString()
    return queryString ? `${intendedPath}?${queryString}` : intendedPath
  }

  /**
   * Removes uid from query params
   * @param {Object} query - Query params object
   * @returns {Object} Query params without uid
   */
  function removeUidFromQuery (query) {
    const newQuery = { ...query }
    delete newQuery.uid
    return newQuery
  }

  /**
   * Builds password-set path with optional tech prefix
   * @param {string} token - Password reset token
   * @param {string|null} techId - Optional tech identifier
   * @returns {string} Password-set path
   */
  function buildPasswordSetPath (token, techId = null) {
    if (!token) {
      throw new Error('Token is required for password-set path')
    }

    const segments = techId ? [techId, 'password-set', token] : ['password-set', token]
    return '/' + segments.join('/')
  }

  return {
    scrollTo,
    loadScript,
    addServiceKey,
    getCookieParams,
    formatPrice,
    formatTitle,
    emphasizeChapter,
    downloadBlob,
    getFlagEmoji,
    scheduleTimeout,
    removeFilesKeysFromLocalStorage,
    formatDate,
    formatDateShort,
    parseMarkdown,
    countdownTransform,
    countdownTransformDaysToHours,
    extractSubstring,
    alternateSplitArray,
    getFlashMessageKey,
    getShortName,
    isEditorPage,
    isPastDate,
    buildIntendedUrl,
    removeUidFromQuery,
    buildPasswordSetPath
  }
}
